import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { fetchMarketplaceProject, fetchMarketplaceProjects } from '../apis';
import { DEFAULT_FILTERS } from '../consts';
import { CategoryFilter, MarketplaceFilters, SortOption } from '../types';

/**
 * Query keys for marketplace
 */
export const marketplaceKeys = {
    all: ['marketplace'] as const,
    lists: () => [...marketplaceKeys.all, 'list'] as const,
    list: (filters: Partial<MarketplaceFilters>) => [...marketplaceKeys.lists(), filters] as const,
    details: () => [...marketplaceKeys.all, 'detail'] as const,
    detail: (id: string) => [...marketplaceKeys.details(), id] as const,
};

/**
 * Hook to fetch marketplace projects with filters and pagination
 */
export const useMarketplaceProjects = (filters?: Partial<MarketplaceFilters>, page = 0, limit = 9) => {
    return useQuery({
        queryKey: marketplaceKeys.list({ ...filters, page, limit }),
        queryFn: () => fetchMarketplaceProjects({ ...filters, page, limit }),
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Hook to fetch a single marketplace project
 */
export const useMarketplaceProject = (id: string) => {
    return useQuery({
        queryKey: marketplaceKeys.detail(id),
        queryFn: () => fetchMarketplaceProject(id),
        enabled: !!id,
        staleTime: 10 * 60 * 1000, // 10 minutes
    });
};

/**
 * Hook to manage marketplace filters with URL sync
 */
export const useMarketplaceFilters = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const filters = useMemo<MarketplaceFilters>(
        () => ({
            searchQuery: searchParams.get('search') || DEFAULT_FILTERS.searchQuery,
            category: (searchParams.get('category') as CategoryFilter) || DEFAULT_FILTERS.category,
            sortBy: (searchParams.get('sort') as SortOption) || DEFAULT_FILTERS.sortBy,
        }),
        [searchParams]
    );

    const page = useMemo(() => parseInt(searchParams.get('page') || '0', 10), [searchParams]);

    const updateFilters = (updates: Partial<MarketplaceFilters>) => {
        const newParams = new URLSearchParams(searchParams);

        // Helper to set or delete param and reset page
        const setFilterParam = (key: string, value: string | undefined, defaultValue?: string) => {
            if (value && value !== defaultValue) {
                newParams.set(key, value);
            } else {
                newParams.delete(key);
            }
            newParams.set('page', '0');
        };

        if (updates.searchQuery !== undefined) {
            setFilterParam('search', updates.searchQuery);
        }

        if (updates.category !== undefined) {
            setFilterParam('category', updates.category, 'all');
        }

        if (updates.sortBy !== undefined) {
            setFilterParam('sort', updates.sortBy, DEFAULT_FILTERS.sortBy);
        }

        setSearchParams(newParams);
    };

    const setPage = (newPage: number) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('page', newPage.toString());
        setSearchParams(newParams);
    };

    const clearFilters = () => {
        setSearchParams({});
    };

    const hasActiveFilters = useMemo(() => {
        return filters.searchQuery !== '' || filters.category !== 'all' || filters.sortBy !== DEFAULT_FILTERS.sortBy;
    }, [filters]);

    return {
        filters,
        page,
        updateFilters,
        setPage,
        clearFilters,
        hasActiveFilters,
    };
};
